import React from "react";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

type WhatsNewItem = {
  id: string;
  text?: string;
  createdAt?: Timestamp | null;
  attachments?: Array<{ url: string; name?: string; type?: string; contentType?: string }>;
  threadId?: string;
  messageId?: string;
  fromUid?: string;
  type?: string;
};

function formatWhen(ts?: Timestamp | null): string {
  try {
    const d = ts && "toDate" in ts ? ts.toDate() : null;
    return d ? d.toLocaleString() : "";
  } catch {
    return "";
  }
}

export default function ScoreboardNews() {
  const [items, setItems] = React.useState<WhatsNewItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [headline, setHeadline] = React.useState<string>("");

  // Live feed from whats_new (latest first)
  React.useEffect(() => {
    const qy = query(collection(db, "whats_new"), orderBy("createdAt", "desc"), limit(100));
    const off = onSnapshot(
      qy,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setLoading(false);
        setError(null);
      },
      (e) => {
        setLoading(false);
        setError(e?.message || "Failed to load What’s New.");
      },
    );
    return () => off();
  }, []);

  // Optional: fetch latest headline from scoreboard_public/news
  React.useEffect(() => {
    (async () => {
      try {
        const s = await getDoc(doc(db, "scoreboard_public", "news"));
        const latest = (s.exists() && (s.data() as any)?.latestText) || "";
        setHeadline(latest || "");
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <main className="max-w-3xl mx-auto p-4">
      <h1 className="text-white text-2xl font-extrabold mb-3">Scoreboard News</h1>

      {headline ? (
        <div className="mb-4 rounded border border-slate-700 bg-black/30 px-3 py-2 text-sm text-slate-200">
          Latest headline: {headline}
        </div>
      ) : null}

      {loading && <div className="text-slate-400">Loading…</div>}
      {error && (
        <div className="rounded border border-red-900 bg-red-950/40 p-3 text-red-200">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-slate-400">
          Nothing yet. Send a DM to “scoreboard-bot” to post an update.
        </div>
      )}

      <div className="space-y-3">
        {items.map((n) => {
          const when = formatWhen(n.createdAt);
          const atts = Array.isArray(n.attachments) ? n.attachments : [];
          return (
            <article
              key={n.id}
              className="rounded border border-slate-800 bg-black/30 p-3"
              aria-label="news item"
            >
              <div className="text-slate-100 whitespace-pre-wrap text-sm">{n.text || ""}</div>

              {atts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {atts.map((a, i) => {
                    const isImg =
                      a?.type === "image" ||
                      String(a?.contentType || "").toLowerCase().startsWith("image/");
                    return isImg ? (
                      <a
                        key={i}
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block w-24 h-24 rounded overflow-hidden border border-white/10"
                        title={a.name || "image"}
                      >
                        <img
                          src={a.url}
                          alt={a.name || ""}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </a>
                    ) : (
                      <a
                        key={i}
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline text-sky-300"
                        title={a.name || "attachment"}
                      >
                        {a.name || "attachment"}
                      </a>
                    );
                  })}
                </div>
              )}

              <div className="mt-2 text-[11px] text-slate-400">
                {when}
                {n.threadId && n.messageId ? (
                  <span className="ml-2 opacity-70">
                    ({n.threadId.slice(0, 6)}…/{n.messageId.slice(0, 6)}…)
                  </span>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </main>
  );
}
